import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Loader2, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: "", company: "", email: "", phone: "", subject: "", message: "" });
  useEffect(() => { document.title = "Contact | Sambiva"; }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm({ full_name: "", company: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-sambiva-charcoal/15 bg-white focus:outline-none focus:ring-2 focus:ring-sambiva-red/40 text-sm";
  const label = "block text-sm font-medium text-sambiva-charcoal mb-1.5";

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-sambiva-charcoal mb-3">{t("contact.title")}</h1>
          <p className="text-sambiva-muted text-lg mb-8">{t("contact.sub")}</p>
          <div className="space-y-5 text-sambiva-charcoal">
            <p className="flex items-start gap-3"><MapPin className="text-sambiva-red mt-1" size={20} /><span>Sambiva · GST Foods<br />Rua das Olarias, Number 10<br />6000-176 Castelo Branco, Portugal</span></p>
            <p className="flex items-center gap-3"><Mail className="text-sambiva-red" size={20} /> hello@sambiva.com</p>
            <p className="flex items-center gap-3"><Phone className="text-sambiva-red" size={20} /> +351 272 000 000</p>
          </div>
        </div>

        <div>
          {done && (
            <div className="mb-5 flex items-center gap-2 bg-sambiva-green/10 text-sambiva-green rounded-xl px-4 py-3 text-sm" data-testid="contact-success">
              <CheckCircle2 size={18} /> {t("contact.success")}
            </div>
          )}
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl p-6 sm:p-8 border border-sambiva-charcoal/5 space-y-5">
            <div>
              <label className={label}>{t("contact.fullName")} *</label>
              <input data-testid="cf-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={field} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={label}>{t("contact.company")}</label><input data-testid="cf-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={field} /></div>
              <div><label className={label}>{t("contact.phone")}</label><input data-testid="cf-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} /></div>
            </div>
            <div><label className={label}>{t("contact.email")} *</label><input data-testid="cf-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={field} /></div>
            <div><label className={label}>{t("contact.subject")} *</label><input data-testid="cf-subject" required value={form.subject} onChange={(e) => set("subject", e.target.value)} className={field} /></div>
            <div><label className={label}>{t("contact.message")} *</label><textarea data-testid="cf-message" rows={5} required value={form.message} onChange={(e) => set("message", e.target.value)} className={field} /></div>
            <button data-testid="cf-submit" disabled={loading} type="submit" className="w-full bg-sambiva-red hover:bg-sambiva-deepred text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={18} />} {t("contact.send")}
            </button>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
